import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { apiUrl } from "../ApiManager/AxiosHeaders";

function ManageNotification() {
  const [permissions, setPermissions] = useState({});
  const [rules, setRules] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [recipientType, setRecipientType] = useState("department");
  const [selectedRecipientId, setSelectedRecipientId] = useState("");

  useEffect(() => {
    const perms = getPermissions();
    if (perms) {
      setPermissions(perms);
    }
  }, []);

  useEffect(() => {
    if (permissions["manage_departments"] === 1) {
      loadNotificationRules();
    }
  }, [permissions]);

  const loadNotificationRules = async () => {
    try {
      const response = await apiUrl.get("/notification-rules");
      if (response?.status) {
        setRules(response?.data?.data?.rules);
        setDepartments(response?.data?.data?.departments);
        setTeams(response?.data?.data?.teams);
        setUsers(response?.data?.data?.users);
      }
    } catch (error) {
      console.error("Error loading rules:", error);
    }
  };

  const handleAddRecipient = async () => {
    if (!selectedRecipientId) return;

    try {
      await apiUrl.post(`/notification-rules/${selectedRuleId}/recipients`, {
        recipient_type: recipientType,
        recipient_id: selectedRecipientId,
      });
      await loadNotificationRules();
      setShowAddModal(false);
      setSelectedRecipientId("");
      Swal.fire("Success", "Recipient added successfully", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to add recipient", "error");
    }
  };

  const handleRemoveRecipient = async (ruleId, recipientId) => {
    try {
      await apiUrl.delete(
        `/notification-rules/${ruleId}/recipients/${recipientId}`
      );
      await loadNotificationRules();
      Swal.fire("Success", "Recipient removed", "success");
    } catch (error) {
      Swal.fire("Error", "Failed to remove recipient", "error");
    }
  };

  const getRecipientName = (recipient) => {
    if (recipient.recipient_type === "department") {
      const dept = departments.find((d) => d.id == recipient.recipient_id);
      return dept ? `${dept.name} (Department)` : "Unknown Department";
    } else if (recipient.recipient_type === "team") {
      const team = teams.find((t) => t.id == recipient.recipient_id);
      return team ? `${team.name} (Team)` : "Unknown Team";
    } else {
      const user = users.find((u) => u.id == recipient.recipient_id);
      return user ? `${user.name} (User)` : "Unknown User";
    }
  };

  const getRecipientOptions = () => {
    if (recipientType === "department") {
      return departments.map((dept) => (
        <option key={dept.id} value={dept.id}>
          {dept.name}
        </option>
      ));
    } else if (recipientType === "team") {
      return teams.map((team) => (
        <option key={team.id} value={team.id}>
          {team.name}
        </option>
      ));
    } else {
      return users.map((user) => (
        <option key={user.id} value={user.id}>
          {user.name} ({user.email})
        </option>
      ));
    }
  };

  if (permissions["manage_departments"] !== 1) {
    return null;
  }

  return (
    <div>
      <header className="border-bottom bg-light">
        <div className="container px-4 py-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold">Notifications Management</h1>
              <p className="text-muted small">Manage Modules Notifications</p>
            </div>
          </div>
        </div>
      </header>

      <div className="container py-4">
        <div className="row">
          {rules?.map((rule) => (
            <div key={rule?.id} className="col-md-4 mb-4">
              <div className="card">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0 text-capitalize fw-bold">
                    {rule?.module_key}
                  </h5>
                </div>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Recipients</h6>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        setSelectedRuleId(rule.id);
                        setShowAddModal(true);
                      }}
                    >
                      Add Recipient
                    </button>
                  </div>

                  {rule?.recipients?.length === 0 ? (
                    <p className="text-muted">No recipients assigned</p>
                  ) : (
                    <div className="list-group">
                      {rule?.recipients.map((recipient) => (
                        <div
                          key={recipient.id}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{getRecipientName(recipient)}</span>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() =>
                              handleRemoveRecipient(rule?.id, recipient?.id)
                            }
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Recipient Modal */}
      {showAddModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={() => setShowAddModal(false)}
          />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title text-white">Add Recipient</h5>
                  <button
                    type="button"
                    className="btn-close btn-light"
                    onClick={() => setShowAddModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Recipient Type</label>
                    <select
                      className="form-select"
                      value={recipientType}
                      onChange={(e) => setRecipientType(e.target.value)}
                    >
                      <option value="department">Department</option>
                      <option value="team">Team</option>
                      <option value="user">User</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      {recipientType === "department"
                        ? "Department"
                        : recipientType === "team"
                        ? "Team"
                        : "User"}
                    </label>
                    <select
                      className="form-select"
                      value={selectedRecipientId}
                      onChange={(e) => setSelectedRecipientId(e.target.value)}
                    >
                      <option value="">Select {recipientType}</option>
                      {getRecipientOptions()}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddRecipient}
                    disabled={!selectedRecipientId}
                  >
                    Add Recipient
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ManageNotification;
